import {
    GREEN_CIRCLE_URL,
    ORANGE_CIRCLE_URL,
    PINK_RED_CIRCLE_URL,
    RM_REVENUE_VS_PORTFOLIO_PNL_RM_LEVEL_TITLE
} from 'utils/charts/constants';
import {
    axisLabelFormatter,
    getHighestAndInterval,
    tooltipForRMLevel
} from './helpers';
import DarkGreyCircle from 'assets/images/dark-grey-circle-icon.svg';

const rmGraphic = (containerWidth) => {
    return {
        image: {
            type: 'image',
            style: {
                image: DarkGreyCircle
            },
            cursor: 'default',
            top: 4,
            right: 0.1 * containerWidth - 10,
            height: '4px',
            width: '4px',
            silent: true
        },
        text: {
            type: 'text',
            style: {
                text: 'RM',
                font: '10px "Outfit" 400',
                fill: '#929292'
            },
            cursor: 'default',
            top: 4,
            right: 0.1 * containerWidth - 45,
            silent: true
        }
    };
};

const defaultViewFullViewGraphic = (
    isRmLevelChartInFullView,
    isRmLevelChartInManualZoom,
    data
) => {
    const { outliers } = { ...data };
    const textToShow =
        isRmLevelChartInManualZoom || !outliers
            ? ''
            : isRmLevelChartInFullView
            ? 'Full View'
            : 'Default View';
    return {
        text: {
            type: 'text',
            style: {
                text: textToShow,
                font: '10px "Outfit" 400',
                fill: '#929292'
            },
            top: 22,
            left: '1%',
            silent: true
        }
    };
};

const moreThanFiveAboveThresholdGraphic = (containerWidth) => {
    return {
        image: {
            type: 'image',
            style: {
                image: GREEN_CIRCLE_URL,
                width: 12,
                height: 12,
                opacity: 0.4
            },
            cursor: 'default',
            bottom: 10,
            left: `${
                ((containerWidth - containerWidth * 0.83) / containerWidth) *
                100
            }%`,
            silent: true
        },
        text: {
            type: 'text',
            style: {
                text: '>5% below Threshold',
                font: '12px "Outfit" 400',
                fill: '#929292'
            },
            cursor: 'default',
            bottom: 10,
            left: `20%`,
            silent: true
        }
    };
};

const lessThanEqualToFivePercentBelowThresholdGraphic = (containerWidth) => {
    return {
        image: {
            type: 'image',
            style: {
                image: ORANGE_CIRCLE_URL,
                width: 12,
                height: 12
            },
            cursor: 'default',
            bottom: 10,
            left: `${
                ((containerWidth - containerWidth * 0.6) / containerWidth) * 100
            }%`,
            silent: true
        },
        text: {
            type: 'text',
            style: {
                text: '\u22645% below Threshold',
                font: '12px "Outfit" 400',
                fill: '#929292'
            },
            cursor: 'default',
            bottom: 10,
            left: `43%`,
            silent: true
        }
    };
};

const aboveThresholdGraphic = (containerWidth) => {
    return {
        image: {
            type: 'image',
            style: {
                image: PINK_RED_CIRCLE_URL,
                width: 12,
                height: 12
            },
            cursor: 'default',
            bottom: 10,
            left: `${
                ((containerWidth - containerWidth * 0.37) / containerWidth) *
                100
            }%`,
            silent: true
        },
        text: {
            type: 'text',
            style: {
                text: 'Above Threshold',
                font: '12px "Outfit" 400',
                fill: '#929292'
            },
            cursor: 'default',
            bottom: 10,
            left: `66%`,
            silent: true
        }
    };
};

export const scenarioOneRmLevelScatterChartOptions = (
    containerOffsetWidth,
    containerOffsetHeight,
    data,
    isRmLevelChartInManualZoom,
    isRmLevelChartInFullView
) => {
    const { outliers } = { ...data };
    const highestNetDelta =
        outliers &&
        Math.max(
            ...outliers.reduce(
                (acc, curr) => [...acc, Math.abs(curr.netDelta)],
                []
            )
        );
    const netDeltaMaxAndInterval = getHighestAndInterval(highestNetDelta, 5);

    const highestRevenue =
        outliers &&
        Math.max(
            ...outliers.reduce(
                (acc, curr) => [...acc, Math.abs(curr.totalRevenue)],
                []
            )
        );
    const revenueMaxAndInterval = getHighestAndInterval(highestRevenue, 10);

    const redRms =
        outliers &&
        outliers.filter((o) => {
            return o.outlier === 1 && o.emergingOutlier === 1;
        });

    const yellowRms =
        outliers &&
        outliers.filter((o) => {
            return o.outlier === 0 && o.emergingOutlier === 1;
        });

    const greenRms =
        outliers &&
        outliers.filter((o) => {
            return o.outlier === 0 && o.emergingOutlier === 0;
        });

    return {
        title: {
            text: RM_REVENUE_VS_PORTFOLIO_PNL_RM_LEVEL_TITLE,
            top: 0,
            left: 'left',
            textStyle: {
                color: '#244B60',
                fontSize: '12px',
                fontWeight: 700
            }
        },
        grid: {
            top: '23%',
            left: '25px',
            width: outliers ? '450px' : '470px',
            height: outliers ? '65%' : '50%',
            containLabel: true
        },
        tooltip: tooltipForRMLevel(outliers),
        xAxis: {
            type: 'value',
            scale: true,
            name: 'Portfolio Unrealized PnL ($)',
            nameLocation: 'center',
            offset: -15,
            nameGap: outliers ? 38 : 78,
            nameTextStyle: {
                color: '#7FADBF',
                fontWeight: 600,
                fontSize: 12,
                width: '100%',
                verticalAlign: 'middle'
            },
            axisLabel: {
                formatter: function (value) {
                    return axisLabelFormatter(
                        value,
                        netDeltaMaxAndInterval.digit,
                        true
                    );
                },
                color: '#929292',
                fontSize: 10,
                fontWeight: 400
            },
            axisLine: {
                show: false
            },
            axisTick: {
                show: false,
                length: 3
            },
            splitLine: {
                show: false
            },
            min: -netDeltaMaxAndInterval.roundedHighest,
            max: netDeltaMaxAndInterval.roundedHighest,
            interval: netDeltaMaxAndInterval.interval
        },
        yAxis: {
            type: 'value',
            name: 'RM Revenue ($)',
            nameLocation: 'center',
            offset: outliers ? -227 : -270,
            nameGap: isRmLevelChartInFullView ? 270 : 277,
            nameTextStyle: {
                color: '#7FADBF',
                fontWeight: 600,
                fontSize: 12,
                width: '100%',
                verticalAlign: 'middle'
            },
            axisLabel: {
                formatter: function (value) {
                    return axisLabelFormatter(
                        value,
                        revenueMaxAndInterval.digit,
                        false
                    );
                },
                color: '#929292',
                fontSize: 10,
                fontWeight: 400
            },
            axisLine: {
                show: false
            },
            axisTick: {
                show: false
            },
            splitLine: {
                show: false
            },
            min: 0,
            max: revenueMaxAndInterval.roundedHighest,
            interval: revenueMaxAndInterval.interval,
            onZero: true
        },
        dataZoom: [
            {
                type: 'inside',
                xAxisIndex: [0],
                startValue: -netDeltaMaxAndInterval.roundedHighest,
                endValue: netDeltaMaxAndInterval.roundedHighest
            },
            {
                type: 'inside',
                yAxisIndex: [0],
                startValue: 0,
                endValue: revenueMaxAndInterval.roundedHighest
            }
        ],
        series: [
            {
                type: 'scatter',
                data: greenRms
                    ? greenRms.map((rm) => {
                          return {
                              id: rm.rmEmployeeId,
                              name: rm.rmEmployeeId,
                              value: [rm.netDelta, rm.totalRevenue]
                          };
                      })
                    : [],
                itemStyle: {
                    color: '#31BC8A'
                },
                symbolSize: 6,
                emphasis: {
                    focus: 'self'
                },
                selectedMode: 'single',
                select: {
                    itemStyle: {
                        borderColor: '#31BC8A',
                        borderWidth: 5,
                        shadowColor: '#31BC8A',
                        shadowBlur: 12
                    }
                }
            },
            {
                type: 'scatter',
                data: yellowRms
                    ? yellowRms.map((rm) => {
                          return {
                              id: rm.rmEmployeeId,
                              name: rm.rmEmployeeId,
                              value: [rm.netDelta, rm.totalRevenue]
                          };
                      })
                    : [],
                itemStyle: {
                    color: '#F8A627'
                },
                symbolSize: 6,
                emphasis: {
                    focus: 'self'
                },
                selectedMode: 'single',
                select: {
                    itemStyle: {
                        borderColor: '#F8A627',
                        borderWidth: 5,
                        shadowColor: '#F8A627',
                        shadowBlur: 12
                    }
                }
            },
            {
                type: 'scatter',
                data: redRms
                    ? redRms.map((rm) => {
                          return {
                              id: rm.rmEmployeeId,
                              name: rm.rmEmployeeId,
                              value: [rm.netDelta, rm.totalRevenue]
                          };
                      })
                    : [],
                itemStyle: {
                    color: '#EB4560'
                },
                symbolSize: 6,
                emphasis: {
                    focus: 'self'
                },
                selectedMode: 'single',
                select: {
                    itemStyle: {
                        borderColor: '#EB4560',
                        borderWidth: 5,
                        shadowColor: '#EB4560',
                        shadowBlur: 12
                    }
                }
            }
        ],
        graphic: [
            rmGraphic(containerOffsetWidth).image,
            rmGraphic(containerOffsetWidth).text,
            defaultViewFullViewGraphic(
                isRmLevelChartInFullView,
                isRmLevelChartInManualZoom,
                data
            ).text,
            moreThanFiveAboveThresholdGraphic(containerOffsetWidth).image,
            moreThanFiveAboveThresholdGraphic(containerOffsetWidth).text,
            lessThanEqualToFivePercentBelowThresholdGraphic(
                containerOffsetWidth
            ).image,
            lessThanEqualToFivePercentBelowThresholdGraphic(
                containerOffsetWidth
            ).text,
            aboveThresholdGraphic(containerOffsetWidth).image,
            aboveThresholdGraphic(containerOffsetWidth).text
        ]
    };
};
